import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, TrendingUp, CheckCircle2, XCircle, Target, Star, RefreshCw, Inbox as InboxIcon, BarChart3, ArrowUpRight, ArrowDownRight, Megaphone, Users, Ticket, Home, Car, Clapperboard, CalendarDays, ShoppingCart, ShoppingBag, Repeat } from "lucide-react";
import { sr } from "../../lib/sr";
import { actAs, auth } from "../../lib/api";
import TrendsPanel from "../../components/smartreach/TrendsPanel";
import MyClients from "./MyClients";
import CompetitorCompare from "../../components/smartreach/CompetitorCompare";

const EV_COLOR = { PROJECTED: "sr-badge-gray", OBSERVED: "sr-badge", MODELED: "sr-badge-amber", VERIFIED: "sr-badge-green" };

const DASH_ICONS = { Sparkles, Megaphone, BarChart3, Users, Ticket, Star, Home, Car, Clapperboard, CalendarDays, ShoppingCart, ShoppingBag, Repeat };

function QuickActions({ workflow }) {
  const nav = useNavigate();
  const actions = workflow?.dashboard_actions || [];
  if (!actions.length) return null;
  const label = workflow?.industry_label;
  return (
    <div style={{ marginTop: 16 }} data-testid="home-quick-actions">
      <div className="sr-eyebrow" style={{ marginBottom: 8 }}>{label ? `Quick actions for ${label}` : "Quick actions"}</div>
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
        {actions.map((a) => {
          const Ico = DASH_ICONS[a.icon] || Sparkles;
          return (
            <button key={a.key} className="sr-card sr-card-hover" onClick={() => nav(a.to)} data-testid={`home-action-${a.key}`}
              style={{ cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}>
              <span className="sr-pill-ico" style={{ background: "var(--blue-light)", color: "var(--blue)" }}><Ico size={18} /></span>
              <strong style={{ color: "var(--navy)", fontSize: 14 }}>{a.label}</strong>
              <ArrowRight size={15} style={{ marginLeft: "auto", color: "var(--muted)" }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WidgetStat({ testid, icon: Ico, label, value, to, cta, nav }) {
  return (
    <div className="sr-card" data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="sr-pill-ico" style={{ background: "var(--blue-light)", color: "var(--blue)" }}><Ico size={16} /></span>
        <div className="sr-eyebrow">{label}</div>
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, color: "var(--navy)", marginTop: 6 }} data-testid={`${testid}-value`}>{value}</div>
      <button className="sr-btn sr-btn-sm" style={{ marginTop: 8 }} onClick={() => nav(to)} data-testid={`${testid}-cta`}>{cta}</button>
    </div>
  );
}

const LinkBtn = ({ onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontWeight: 700, padding: 0, font: "inherit" }}>{children}</button>
);

function DashboardWidgets({ data }) {
  const nav = useNavigate();
  const wf = data.workflow || {};
  const d = data.dashboard || {};
  const widgets = wf.home_widgets || [];
  if (!widgets.length) return null;
  const cards = widgets.map((w) => {
    if (w === "release_countdown") {
      const ups = d.upcoming_releases || [];
      const shown = ups.slice(0, 3);
      return (
        <div key="rc" className="sr-card" data-testid="widget-release-countdown" style={{ gridColumn: "span 2" }}>
          <div className="sr-eyebrow" style={{ marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}><CalendarDays size={13} /> Upcoming releases</div>
          {shown.length ? shown.map((r, i) => (
            <div key={i} data-testid={`widget-release-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < shown.length - 1 ? "1px solid var(--border)" : "none" }}>
              <div><strong style={{ color: "var(--navy)", fontSize: 14 }}>{r.movie_name}</strong>
                <div className="sr-muted" style={{ fontSize: 12, textTransform: "capitalize" }}>{r.release_type || "theatrical"}{r.markets?.length ? ` · ${r.markets.slice(0, 2).join(", ")}` : ""}</div></div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 800, color: "var(--blue)", fontSize: 18 }}>{r.days_to_open != null ? `${r.days_to_open}d` : "—"}</div>
                <div className="sr-muted" style={{ fontSize: 11 }}>{r.release_date}</div></div>
            </div>
          )) : <div className="sr-muted" style={{ fontSize: 13 }}>No upcoming releases scheduled. <LinkBtn testid="widget-release-cta" onClick={() => nav("/app/release-calendar")}>Plan a release →</LinkBtn></div>}
        </div>
      );
    }
    if (w === "movie_catalog") return <WidgetStat key="mc" testid="widget-movie-catalog" icon={Clapperboard} label="Movies in your catalog" value={d.movies || 0} to="/app/movie-studio" cta="Open Movie Studio" nav={nav} />;
    if (w === "catalog") return <WidgetStat key="cat" testid="widget-catalog" icon={ShoppingBag} label={wf.catalog_label || "Catalog"} value={d.catalog_total || 0} to="/app/catalog" cta="Manage" nav={nav} />;
    if (w === "offers") return <WidgetStat key="off" testid="widget-offers" icon={Ticket} label="Active offers" value={d.offers_active || 0} to="/app/offers" cta="Create an offer" nav={nav} />;
    if (w === "loyalty") return <WidgetStat key="loy" testid="widget-loyalty" icon={Star} label="Loyalty members" value={d.loyalty_members || 0} to="/app/loyalty" cta="Loyalty" nav={nav} />;
    if (w === "leads") return <WidgetStat key="lead" testid="widget-leads" icon={Users} label="Leads" value={d.leads_open || 0} to="/app/leads" cta="View leads" nav={nav} />;
    if (w === "latest_plan") {
      const lp = data.latest_plan;
      const fc = lp?.plan?.forecast;
      return (
        <div key="lp" className="sr-card" data-testid="widget-latest-plan">
          <div className="sr-eyebrow" style={{ marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={13} /> Latest growth plan</div>
          {lp ? <>
            <strong style={{ color: "var(--navy)", fontSize: 14 }}>v{lp.version} · {lp.status || "DRAFT"}</strong>
            {fc && <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 4 }} data-testid="widget-latest-plan-forecast">Forecast: {fc.low}–{fc.high} {fc.unit || fc.metric}</div>}
            <button className="sr-btn sr-btn-sm" style={{ marginTop: 10 }} onClick={() => nav("/app/growth-plan")} data-testid="widget-latest-plan-cta">Open plan</button>
          </> : <div className="sr-muted" style={{ fontSize: 13 }}>No plan yet. <LinkBtn testid="widget-plan-cta" onClick={() => nav("/app/growth-plan")}>Build one →</LinkBtn></div>}
        </div>
      );
    }
    return null;
  });
  return (
    <div style={{ marginTop: 16 }} data-testid="home-dashboard-widgets">
      <div className="sr-eyebrow" style={{ marginBottom: 8 }}>{wf.industry_label ? `${wf.industry_label} dashboard` : "Your dashboard"}</div>
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>{cards}</div>
    </div>
  );
}

function MiniBars({ data, metric, color }) {
  const max = Math.max(1, ...data.map((d) => d[metric] || 0));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 46 }}>
      {data.map((d, i) => (
        <div key={i} title={`${d.date}: ${(d[metric] || 0).toLocaleString()} ${metric}`}
          style={{ flex: 1, minWidth: 2, background: color, borderRadius: 2,
            height: `${Math.max(4, ((d[metric] || 0) / max) * 100)}%`, opacity: d.phase === "before" ? 0.35 : 1 }} />
      ))}
    </div>
  );
}

function Delta({ pct }) {
  if (pct == null) return <span className="sr-muted" style={{ fontSize: 12 }}>—</span>;
  const up = pct >= 0;
  const C = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontWeight: 700, fontSize: 13, color: up ? "var(--success)" : "#B42318" }}>
      <C size={14} /> {up ? "+" : ""}{pct}%
    </span>
  );
}

function AnalyticsCards() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [uplift, setUplift] = useState(null);
  useEffect(() => { sr.ga4Status().then(setStatus).catch(() => setStatus({ configured: false })); }, []);
  useEffect(() => {
    if (status?.connected && status?.property_id) {
      sr.ga4Report().then(setReport).catch(() => {});
      sr.ga4Uplift().then(setUplift).catch(() => {});
    }
  }, [status?.connected, status?.property_id]);

  if (!status || !status.configured) return null;

  if (!status.connected || !status.property_id) {
    return (
      <div className="sr-card" style={{ marginTop: 16, borderLeft: "4px solid #E8710A", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="analytics-connect-nudge">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="sr-pill-ico" style={{ background: "#FEF0E6", color: "#E8710A" }}><BarChart3 size={18} /></span>
          <div>
            <div style={{ color: "var(--navy)", fontWeight: 700 }}>See your real website traffic</div>
            <div className="sr-muted" style={{ fontSize: 13 }}>Connect Google Analytics to track sessions, top pages and campaign uplift here.</div>
          </div>
        </div>
        <Link to="/app/onboarding" className="sr-btn sr-btn-primary sr-btn-sm" data-testid="analytics-connect-link">Connect in Setup <ArrowRight size={14} /></Link>
      </div>
    );
  }

  const daily = report?.daily || [];
  const cU = uplift?.campaign;
  const yoy = uplift?.yoy;
  return (
    <>
      <div className="sr-card" style={{ marginTop: 16 }} data-testid="analytics-trend-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 18, margin: 0 }}><BarChart3 size={16} /> Website traffic</h2>
          <span className="sr-muted" style={{ fontSize: 12 }}>{report?.period} · {status.property_name || `#${status.property_id}`}</span>
        </div>
        <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span className="sr-stat-val" data-testid="analytics-sessions">{(report?.summary?.sessions || 0).toLocaleString()}</span>
              <span className="sr-muted" style={{ fontSize: 12 }}>sessions</span>
            </div>
            <div style={{ marginTop: 6 }}><MiniBars data={daily} metric="sessions" color="var(--blue)" /></div>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span className="sr-stat-val" data-testid="analytics-users">{(report?.summary?.users || 0).toLocaleString()}</span>
              <span className="sr-muted" style={{ fontSize: 12 }}>users</span>
            </div>
            <div style={{ marginTop: 6 }}><MiniBars data={daily} metric="users" color="#E8710A" /></div>
          </div>
        </div>
        {(report?.top_pages || []).length > 0 && (
          <div style={{ marginTop: 14 }} data-testid="analytics-top-pages">
            <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Top pages</div>
            {report.top_pages.slice(0, 5).map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{p.page}</span>
                <span className="sr-muted">{(p.sessions || 0).toLocaleString()} sessions</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {uplift && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="analytics-uplift-card">
          <h2 style={{ fontSize: 18, margin: 0 }}><TrendingUp size={16} /> Uplift</h2>
          <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
            <div data-testid="analytics-campaign-uplift">
              <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Since your last campaign launched</div>
              {cU ? (
                <>
                  <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6, textTransform: "capitalize" }}>{cU.goal} · launched {cU.launch_date} · {cU.window_days}-day windows</div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13 }}>Sessions <Delta pct={cU.uplift?.sessions} /></span>
                    <span style={{ fontSize: 13 }}>Users <Delta pct={cU.uplift?.users} /></span>
                    <span style={{ fontSize: 13 }}>Conversions <Delta pct={cU.uplift?.conversions} /></span>
                  </div>
                  {(cU.daily || []).length > 0 && <div style={{ marginTop: 10 }}><MiniBars data={cU.daily} metric="sessions" color="var(--blue)" /><div className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>Faded = before launch · solid = after</div></div>}
                </>
              ) : (
                <p className="sr-muted" style={{ fontSize: 13 }}>Launch a campaign to see its uplift here.</p>
              )}
            </div>
            <div data-testid="analytics-yoy">
              <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Year over year</div>
              <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>{yoy?.period_label}</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13 }}>Sessions <Delta pct={yoy?.uplift?.sessions} /></span>
                <span style={{ fontSize: 13 }}>Users <Delta pct={yoy?.uplift?.users} /></span>
                <span style={{ fontSize: 13 }}>Conversions <Delta pct={yoy?.uplift?.conversions} /></span>
              </div>
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                {(yoy?.current?.sessions || 0).toLocaleString()} vs {(yoy?.prior_year?.sessions || 0).toLocaleString()} sessions
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TrustBadge({ profile }) {
  const revs = (profile.reviews || []).filter((r) => r.text);
  const initial = (profile.featured_review_index != null && profile.featured_review_index < revs.length)
    ? profile.featured_review_index
    : revs.map((r, i) => [r, i]).filter(([r]) => (r.rating || 0) >= 4).sort((a, b) => (b[0].text || "").length - (a[0].text || "").length).map(([, i]) => i)[0];
  const [sel, setSel] = useState(initial != null ? initial : (revs.length ? 0 : null));
  const [picking, setPicking] = useState(false);
  const shown = sel != null ? revs[sel] : null;
  const choose = async (i) => {
    setSel(i); setPicking(false);
    try { await sr.setFeaturedReview(i); toast.success("Featured review updated"); }
    catch { toast.error("Couldn't save your pick"); }
  };
  return (
    <div className="sr-card" style={{ marginTop: 16, borderLeft: "4px solid var(--gold)" }} data-testid="google-trust-badge">
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 92 }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: "var(--navy)", lineHeight: 1 }} data-testid="trust-rating">{profile.rating}</div>
          <div style={{ display: "flex", gap: 1, marginTop: 4 }}>
            {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} style={{ color: "#E0A94A", fill: i <= Math.round(profile.rating) ? "#E0A94A" : "none" }} />)}
          </div>
          {profile.rating_count ? <div className="sr-muted" style={{ fontSize: 11, marginTop: 3 }}>{profile.rating_count} Google reviews</div> : null}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--blue)" }}>Your Google reputation</div>
            {revs.length > 1 && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setPicking((p) => !p)} data-testid="trust-change-review">{picking ? "Close" : "Change review"}</button>}
          </div>
          {shown ? <p style={{ fontSize: 14, color: "var(--navy)", marginTop: 6, fontStyle: "italic" }} data-testid="trust-review">“{String(shown.text).slice(0, 180)}{String(shown.text).length > 180 ? "…" : ""}”</p>
            : <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>Great rating — we'll use it to strengthen your ads.</p>}
        </div>
      </div>
      {picking && (
        <div style={{ marginTop: 12, display: "grid", gap: 8 }} data-testid="trust-review-picker">
          {revs.map((r, i) => (
            <button key={i} onClick={() => choose(i)} data-testid={`trust-review-option-${i}`}
              style={{ textAlign: "left", background: i === sel ? "var(--blue-light)" : "#fff", border: `1px solid ${i === sel ? "var(--blue)" : "var(--border)"}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", fontSize: 13, color: "var(--navy)" }}>
              <b>{r.rating ? `${r.rating}★` : ""}</b> {String(r.text).slice(0, 150)}{String(r.text).length > 150 ? "…" : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppHome() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [inbox, setInbox] = useState(null);
  const staff = ["admin", "super_admin", "campaign_manager", "compliance_reviewer"].includes(auth.getUser()?.role);
  const tenantScoped = !!actAs.get() || !staff;
  const load = () => {
    if (!tenantScoped) { setData({ needsClient: true }); return; }
    sr.home().then(setData).catch(() => toast.error("Could not load your workspace"));
    sr.inbox().then(setInbox).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (data?.needsClient) {
    return <MyClients embedded />;
  }

  const act = async (id, approve) => {
    try {
      approve ? await sr.approveRec(id) : await sr.rejectRec(id);
      toast.success(approve ? "Recommendation approved" : "Recommendation dismissed");
      load();
    } catch { toast.error("Action failed"); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const nba = data.next_best_action;
  const results = data.results_preview || [];

  return (
    <div>
      <h1 style={{ fontSize: 30 }}>Home</h1>
      <p className="sr-muted" style={{ marginTop: 6 }}>Your daily growth brief — what's happening and what to do next.</p>

      {/* Next best action */}
      <div className="sr-card" style={{ marginTop: 22, background: "var(--navy)", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <span className="sr-pill-ico" style={{ background: "var(--gold)", color: "var(--navy)" }}><Sparkles size={20} /></span>
          <div>
            <div style={{ color: "#9fb2d6", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em" }}>Next best action</div>
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 700, marginTop: 2 }}>{nba.title}</div>
          </div>
        </div>
        <button className="sr-btn sr-btn-gold" onClick={() => nav(nba.to)} data-testid="next-action-btn">{nba.cta} <ArrowRight size={16} /></button>
      </div>

      {/* Per-industry quick actions */}
      <QuickActions workflow={data.workflow} />
      <DashboardWidgets data={data} />

      {/* Finish-setup resume card (signups who started but didn't finish) */}
      {data.setup_resume && (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderLeft: "4px solid #F59E0B" }} data-testid="home-setup-resume">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="sr-pill-ico" style={{ background: "#FEF3C7", color: "#B45309" }}><Sparkles size={18} /></span>
            <div>
              <div style={{ color: "var(--navy)", fontWeight: 700 }}>Finish setting up — you're on step {data.setup_resume.step}</div>
              <div className="sr-muted" style={{ fontSize: 13 }}>Pick up where you left off and we'll build your AI growth plan.{data.setup_resume.industry ? ` · ${data.setup_resume.industry}` : ""}</div>
            </div>
          </div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => nav("/app/onboarding")} data-testid="home-setup-resume-btn">Resume setup <ArrowRight size={14} /></button>
        </div>
      )}

      {/* Inbox quick-glance */}
      {inbox && (inbox.counts?.leads > 0 || inbox.counts?.approvals > 0) && (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderLeft: "4px solid var(--blue)" }} data-testid="home-inbox-card">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="sr-pill-ico" style={{ background: "var(--blue-light)", color: "var(--blue)" }}><InboxIcon size={18} /></span>
            <div>
              <div style={{ color: "var(--navy)", fontWeight: 700 }}>In your inbox</div>
              <div className="sr-muted" style={{ fontSize: 13 }}>
                {inbox.counts.leads > 0 && <span data-testid="home-inbox-leads"><b style={{ color: "var(--navy)" }}>{inbox.counts.leads}</b> new lead{inbox.counts.leads === 1 ? "" : "s"}</span>}
                {inbox.counts.leads > 0 && inbox.counts.approvals > 0 && " · "}
                {inbox.counts.approvals > 0 && <span data-testid="home-inbox-approvals"><b style={{ color: "var(--navy)" }}>{inbox.counts.approvals}</b> awaiting approval</span>}
              </div>
            </div>
          </div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => nav("/app/inbox")} data-testid="home-inbox-btn">Open inbox <ArrowRight size={14} /></button>
        </div>
      )}

      {/* Google reputation trust badge */}
      {data.profile?.rating != null && <TrustBadge profile={data.profile} />}

      {/* Website traffic + campaign uplift (Google Analytics) */}
      <AnalyticsCards />

      {/* 5-question grid */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", marginTop: 20 }}>
        <div className="sr-stat"><div className="sr-stat-label">What we're pursuing</div>
          <div className="sr-stat-val" style={{ fontSize: 22, textTransform: "capitalize" }} data-testid="home-goal-label">{data.goal_label || data.goal || "Not set"}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Live / approved plans</div>
          <div className="sr-stat-val">{data.approved_count}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Locations</div>
          <div className="sr-stat-val">{data.locations}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Awaiting your approval</div>
          <div className="sr-stat-val">{(data.recommendations || []).length}</div></div>
      </div>

      {/* Trending competitor creative by category */}
      <h2 style={{ fontSize: 20, marginTop: 34 }}>Trending for your competitors</h2>
      <TrendsPanel limit={3} />

      {/* What did it produce */}
      <h2 style={{ fontSize: 20, marginTop: 34 }}>What did it produce?</h2>
      {results.length ? (
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", marginTop: 14 }}>
          {results.map((r) => (
            <div key={r.metric} className="sr-card">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className={`sr-badge ${EV_COLOR[r.evidence] || "sr-badge-gray"}`}>{r.evidence}</span>
                <span style={{ color: "var(--success)", fontWeight: 700, fontSize: 13 }}><TrendingUp size={13} /> {r.trend}</span>
              </div>
              <div className="sr-stat-val" style={{ marginTop: 10 }}>{r.value.toLocaleString()}</div>
              <div className="sr-stat-label">{r.metric}</div>
              {r.cost_per != null && <div className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>${r.cost_per} each</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="sr-card sr-empty" style={{ marginTop: 14 }} data-testid="results-empty">
          <Target size={26} style={{ opacity: .4 }} />
          <p style={{ marginTop: 10 }}>Results appear here once an approved plan is live and delivering.</p>
          <Link to="/app/growth-plan" className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 14 }}>Build a growth plan</Link>
        </div>
      )}

      {/* Recommendations */}
      <h2 style={{ fontSize: 20, marginTop: 34 }}>What changed — AI recommendations</h2>
      {(data.recommendations || []).length ? (
        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          {data.recommendations.map((r) => (
            <div key={r.id} className="sr-card" data-testid={`rec-${r.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <span className="sr-badge">{r.agent_type}</span>
                  <span className="sr-badge sr-badge-gray" style={{ marginLeft: 6 }}>Confidence: {r.confidence}</span>
                  <h3 style={{ fontSize: 17, marginTop: 10 }}>{r.title}</h3>
                  <p className="sr-muted" style={{ marginTop: 6, fontSize: 14 }}>{r.summary}</p>
                  {r.expected_impact?.metric && (
                    <p style={{ fontSize: 13, marginTop: 8, color: "var(--navy)", fontWeight: 600 }}>
                      Expected: {r.expected_impact.metric}
                      {r.expected_impact.low != null && ` ${r.expected_impact.low}–${r.expected_impact.high}${r.expected_impact.unit || ""}`}
                      {" · "}Requires: {r.required_permission}
                    </p>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => act(r.id, true)} data-testid={`rec-approve-${r.id}`}><CheckCircle2 size={15} /> Approve</button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => act(r.id, false)} data-testid={`rec-reject-${r.id}`}><XCircle size={15} /> Dismiss</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="sr-card sr-empty" style={{ marginTop: 14 }}>No recommendations right now — you're all caught up.</div>
      )}

      <CompetitorCompare title="Competitor watch — rating &amp; review trend" />
    </div>
  );
}
